import { Controller } from "@hotwired/stimulus"
import { animate } from "motion"

/**
 * Snow Melting Calculator Controller
 * Calculates operating costs for snow melting systems
 * 
 * Formula: 
 *   Full coverage: sqft = width * length
 *   Tire tracks: sqft = tracks * width * length
 *   hourly_cost = (kwh * sqft * 50 / 1000) / 100
 *   total_cost = hourly_cost * hours
 */
export default class extends Controller {
  static targets = [
    "widthInput", "lengthInput", "tracksInput",
    "hoursSelect", "hoursDisplay",
    "kwhInput", "zipInput",
    "hourlyCost", "totalCost",
    "sizeSelect", "coverageSelect",
    "tracksColumn", "widthColumn",
    "fullCoverageImage", "tireTracksImage",
    "drivewaySizeIcon"
  ]

  static values = {
    width: { type: Number, default: 10 },
    length: { type: Number, default: 20 },
    tracks: { type: Number, default: 2 },
    hours: { type: Number, default: 6 },
    kwh: { type: Number, default: 16.26 },
    tireTracks: { type: Boolean, default: true },
    wattsPerSqft: { type: Number, default: 50 }
  }

  connect() {
    this.updateDimensionsUI()
    this.updateCoverageImage()
    this.updateHoursDisplay()
    this.initDrivewaySizeIcon()
    this.calculate()
  }

  initDrivewaySizeIcon() {
    if (this.hasSizeSelectTarget) {
      const selectedOption = this.sizeSelectTarget.options[this.sizeSelectTarget.selectedIndex]
      const cars = selectedOption.dataset.cars
      const isCustom = selectedOption.value === "custom"
      this.updateDrivewaySizeIcon(cars)
      this.updateFieldsEditability(isCustom)
    }
  }

  // Calculate and update display
  calculate() {
    const sqft = this.calculateSqft()
    const kwh = this.kwhValue
    const hours = this.getHours()
    const wattsPerSqft = this.wattsPerSqftValue

    // Formula: (kwh * sqft * wattsPerSqft / 1000) / 100
    const hourlyCost = (kwh * sqft * wattsPerSqft / 1000) / 100
    const totalCost = hourlyCost * hours

    this.updateCostDisplay(hourlyCost, totalCost)
  }

  calculateSqft() {
    if (this.tireTracksValue) {
      const tracks = this.hasTracksInputTarget ? parseFloat(this.tracksInputTarget.value) || this.tracksValue : this.tracksValue
      const width = this.hasWidthInputTarget ? parseFloat(this.widthInputTarget.value) || this.widthValue : this.widthValue
      const length = this.hasLengthInputTarget ? parseFloat(this.lengthInputTarget.value) || this.lengthValue : this.lengthValue
      return tracks * width * length
    } else {
      // Full coverage: width is the full driveway width
      const width = this.hasWidthInputTarget ? parseFloat(this.widthInputTarget.value) || this.widthValue : this.widthValue
      const length = this.hasLengthInputTarget ? parseFloat(this.lengthInputTarget.value) || this.lengthValue : this.lengthValue
      return width * length
    }
  }

  getHours() {
    if (this.hasHoursSelectTarget) {
      this.hoursValue = parseInt(this.hoursSelectTarget.value) || this.hoursValue
    }
    return this.hoursValue
  }

  updateCostDisplay(hourly, total) {
    const costTargets = []
    
    if (this.hasHourlyCostTarget) {
      this.hourlyCostTarget.textContent = hourly.toFixed(2)
      costTargets.push(this.hourlyCostTarget.parentElement)
    }
    if (this.hasTotalCostTarget) {
      this.totalCostTarget.textContent = total.toFixed(2)
      costTargets.push(this.totalCostTarget.parentElement)
    }
    
    // Animate the cost values with a pop effect
    this.animateCostChange(costTargets)
  }

  animateCostChange(targets) {
    targets.forEach((target) => {
      animate(
        target,
        { scale: [1, 1.15, 1], opacity: [0.7, 1] },
        { duration: 0.3, easing: "ease-out" }
      )
    })
  }

  // Hours dropdown selection
  selectHours() {
    this.hoursValue = parseInt(this.hoursSelectTarget.value) || this.hoursValue
    this.updateHoursDisplay()
    this.calculate()
  }

  updateHoursDisplay() {
    if (this.hasHoursDisplayTarget) {
      this.hoursDisplayTarget.textContent = this.hoursValue
    }
  }

  // Coverage selection (dropdown)
  selectCoverage() {
    if (!this.hasCoverageSelectTarget) return
    const value = this.coverageSelectTarget.value
    this.tireTracksValue = (value === "tire_tracks")
    this.updateDimensionsUI()
    this.updateCoverageImage()
    this.calculate()
  }

  updateDimensionsUI() {
    // Show/hide tracks column based on coverage type
    if (this.hasTracksColumnTarget) {
      this.tracksColumnTarget.classList.toggle("d-none", !this.tireTracksValue)
    }
    // Adjust width column size
    if (this.hasWidthColumnTarget) {
      if (this.tireTracksValue) {
        this.widthColumnTarget.classList.remove("col-6")
        this.widthColumnTarget.classList.add("col-4")
      } else {
        this.widthColumnTarget.classList.remove("col-4")
        this.widthColumnTarget.classList.add("col-6")
      }
    }
  }

  updateCoverageImage() {
    // Toggle images based on coverage type
    if (this.hasFullCoverageImageTarget) {
      this.fullCoverageImageTarget.classList.toggle("d-none", this.tireTracksValue)
    }
    if (this.hasTireTracksImageTarget) {
      this.tireTracksImageTarget.classList.toggle("d-none", !this.tireTracksValue)
    }
  }

  // Driveway size selection (dropdown)
  selectDrivewaySize(event) {
    const select = event ? event.currentTarget : this.sizeSelectTarget
    const selectedOption = select.options[select.selectedIndex]
    const tracks = selectedOption.dataset.tracks
    const length = selectedOption.dataset.length
    const width = selectedOption.dataset.width
    const cars = selectedOption.dataset.cars
    const isCustom = selectedOption.value === "custom"

    // Update car icons
    this.updateDrivewaySizeIcon(cars)

    // Update values if not custom
    if (!isCustom && tracks && tracks !== "null" && tracks !== "") {
      this.tracksValue = parseInt(tracks)
      this.lengthValue = parseInt(length)
      this.widthValue = parseInt(width)

      // Update input fields
      if (this.hasTracksInputTarget) this.tracksInputTarget.value = tracks
      if (this.hasLengthInputTarget) this.lengthInputTarget.value = length
      if (this.hasWidthInputTarget) this.widthInputTarget.value = width
    }

    // Make tracks field read-only for preset sizes, editable for custom
    this.updateFieldsEditability(isCustom)
    
    this.calculate()
  }

  updateFieldsEditability(isCustom) {
    // Tracks field: read-only for presets, editable for custom
    if (this.hasTracksInputTarget) {
      this.tracksInputTarget.readOnly = !isCustom
      this.tracksInputTarget.classList.toggle("bg-light", !isCustom)
    }
  }

  updateDrivewaySizeIcon(cars) {
    if (!this.hasDrivewaySizeIconTarget) return
    
    const numCars = parseInt(cars) || 0
    let iconHtml = ''
    
    if (numCars === 0) {
      // Custom - show pencil icon
      iconHtml = '<i class="fa-light fa-pencil fa-2x text-muted"></i>'
    } else {
      // Show car icons based on number
      for (let i = 0; i < numCars; i++) {
        iconHtml += '<i class="fa-light fa-car fa-2x text-muted"></i> '
      }
    }
    
    this.drivewaySizeIconTarget.innerHTML = iconHtml
  }

  // Location methods
  useGeolocation() {
    if (!navigator.geolocation) {
      console.warn("Geolocation not supported")
      return
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords
        await this.fetchRateByCoords(latitude, longitude)
      },
      (error) => {
        console.error("Geolocation error:", error)
      },
      { enableHighAccuracy: false, timeout: 10000 }
    )
  }

  async lookupZipCode() {
    if (!this.hasZipInputTarget) return
    const zip = this.zipInputTarget.value.trim()
    if (!this.isValidZipCode(zip)) return

    await this.fetchRateByZip(zip)
  }

  updateKwh() {
    const kwh = parseFloat(this.kwhInputTarget.value) || this.kwhValue
    this.kwhValue = kwh
    this.calculate()
  }

  // API calls
  async fetchRateByCoords(lat, lng) {
    try {
      const response = await fetch(`/api/v1/locations/by_lat_lng?lat=${lat}&lng=${lng}`)
      if (!response.ok) {
        const errorText = await response.text()
        console.error("Failed to fetch rate:", response.status, errorText)
        throw new Error(`Failed to fetch rate: ${response.status}`)
      }
      
      const data = await response.json()
      console.log("Geolocation API response:", data)
      
      // Fill in zip code if available
      if (data.postal_code && this.hasZipInputTarget) {
        this.zipInputTarget.value = data.postal_code
      }
      
      const rate = this.extractRate(data)
      this.setKwhRate(rate)
    } catch (error) {
      console.error("Error fetching rate by coords:", error)
      // Continue with current/default rate if API fails
    }
  }

  async fetchRateByZip(zip) {
    try {
      const response = await fetch(`/api/v1/locations/by_postal_code/${zip}`)
      if (!response.ok) throw new Error("Failed to fetch rate")
      
      const data = await response.json()
      const rate = this.extractRate(data)
      this.setKwhRate(rate)
    } catch (error) {
      console.error("Error fetching rate by zip:", error)
    }
  }

  extractRate(data) {
    // API returns rate in dollars, convert to cents
    const rate = data.average_rate ? parseFloat((data.average_rate * 100).toFixed(2)) : this.kwhValue
    return rate
  }

  setKwhRate(rate) {
    this.kwhValue = rate
    if (this.hasKwhInputTarget) {
      this.kwhInputTarget.value = rate.toFixed(2)
    }
    this.calculate()
  }

  // Zip code validation (US and Canadian)
  isValidZipCode(zip) {
    const usOrCaZipcode = /(^\d{5}(-\d{4})?$)|(^[ABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Z]{1} *\d{1}[A-Z]{1}\d{1}$)/i
    return usOrCaZipcode.test(zip)
  }
}
